import { definePostHogExperiment } from "../../../jsutils/src/posthogExperiments"
import type { IPostHogExperimentContract } from "../../../jsutils/src/posthogExperiments"

import { EVMAnalyticsEvent } from "./events"

/**
 * VidMax-owned experiment contracts.
 *
 * Feature flags that are not experiments remain in featureFlags.ts.
 */
export const VM_EXPERIMENTS = {
  ASSIGNMENT_LAB_CANARY: definePostHogExperiment({
    key: "exp-vidmax-assignment-lab-canary",
    displayName: "VidMax Assignment Lab Canary",
    variants: ["control", "treatment"] as const,
    fallbackVariant: "control",
    goalEvent: EVMAnalyticsEvent.DEBUG_TEST,
    measurementEnvironment: "prod",
    hypothesis: "The assignment lab resolves the same forced variant in browser and API code paths.",
    owner: "vidmax-devex",
  }),
} as const satisfies Readonly<Record<string, IPostHogExperimentContract>>

export type TVidmaxExperimentKey = keyof typeof VM_EXPERIMENTS
export type TVidmaxExperiment<K extends TVidmaxExperimentKey = TVidmaxExperimentKey> = (typeof VM_EXPERIMENTS)[K]
export type TVidmaxExperimentVariant<K extends TVidmaxExperimentKey> =
  TVidmaxExperiment<K> extends {
    readonly variants: readonly string[]
  }
    ? TVidmaxExperiment<K>["variants"][number]
    : string

/** Resolve a contract by its registry key for typed product-code accessors. */
export const getVidmaxExperimentByRegistryKey = (key: TVidmaxExperimentKey): IPostHogExperimentContract => {
  const registry: Readonly<Record<string, IPostHogExperimentContract>> = VM_EXPERIMENTS
  const experiment = registry[key]
  if (!experiment) throw new Error(`Unknown VidMax experiment registry key: ${String(key)}`)
  return experiment
}

/** Find a VidMax contract without widening an untrusted key into the typed accessor surface. */
export const getVidmaxExperiment = (key: string): IPostHogExperimentContract | undefined => {
  const registry: Readonly<Record<string, IPostHogExperimentContract>> = VM_EXPERIMENTS
  return Object.values(registry).find((experiment) => experiment.key === key)
}
